import {
    useRef,
    useState
} from 'react';

import { SimpleGrid, Text } from '@mantine/core';

import { usePaymentProfiles } from '../hooks/usePaymentProfiles.ts';
import { usePaymentProfilesSelection } from '../hooks/usePaymentProfilesSelection.ts';
import { FormDrawer } from '../drawers/FormDrawer.tsx';
import { update } from '../services/PaymentProfilesService.ts';
import { PaymentProfileStatusBadge } from '../profile/PaymentProfileStatusBadge.tsx';
import TableFormattedTimestamp from '../../../utils/TableFormattedTimestamp.tsx';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import { DataTableColumn } from "mantine-datatable";
import { DataTableTokenPagination } from "../../../components/DataTableTokenPagination.tsx";
import { PaymentProfile } from '../types';
import { useQueryClient } from '@tanstack/react-query';

export const RecordsTable = ({ customerId, billingProviderId }: { customerId?: string; billingProviderId?: string }) => {
    const {selectedRecords, setSelectedRecords} = usePaymentProfilesSelection();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<PaymentProfile | null>(null);
    const queryClient = useQueryClient();

    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = usePaymentProfiles({ customerId, billingProviderId });

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const handleRecordClick = (record: PaymentProfile) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: PaymentProfile) => {
        if (selectedRecord?.id) {
            await update(selectedRecord.id, values);
            queryClient.invalidateQueries({ queryKey: ['payment-profiles'] });
        }
    };

    const columns: DataTableColumn<PaymentProfile>[] = [
        {
            accessor: 'name',
            title: 'Payment Profile',
            render: (record: PaymentProfile) => (
                <Text
                    c="blue"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleRecordClick(record)}
                >
                    {record.name}
                </Text>
            )
        },
        ...(billingProviderId ? [{
            accessor: 'customer.name',
            title: 'Customer',
            render: (record: PaymentProfile) => (
                <Text>
                    {record.customer?.name || 'N/A'}
                </Text>
            )
        }] : [{
            accessor: 'billingProvider.name',
            title: 'Provider',
            render: (record: PaymentProfile) => (
                <Text>
                    {record.billingProvider?.name || 'N/A'}
                </Text>
            )
        }]) as DataTableColumn<PaymentProfile>[],
        {
            accessor: 'status',
            title: 'Status',
            render: (record: PaymentProfile) => (
                <PaymentProfileStatusBadge status={record.status} />
            )
        },
        {
            accessor: 'created',
            title: 'Created',
            render: (record: PaymentProfile) => (
                <TableFormattedTimestamp record={record} propertyName="created" />
            )
        },
    ];



    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword} />

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken} handleLoadMore={fetchRecords} />

            <FormDrawer
                record={selectedRecord || {} as PaymentProfile}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};